import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCompactNumber } from '../../lib/formatters'
import { calculateFIRE } from '../../lib/calculations/fire'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint, step, min, max }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function FIRECalculator() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [currentAge, setCurrentAge] = useState(30)
  const [targetRetirementAge, setTargetRetirementAge] = useState(45)
  const [annualExpenses, setAnnualExpenses] = useState(50000)
  const [currentInvestments, setCurrentInvestments] = useState(100000)
  const [annualSavings, setAnnualSavings] = useState(40000)
  const [expectedReturn, setExpectedReturn] = useState(8)
  const [safeWithdrawalRate, setSafeWithdrawalRate] = useState(4)

  const result = useMemo(() => calculateFIRE({
    currentAge, targetRetirementAge, annualExpenses, currentInvestments,
    annualSavings, expectedReturn, safeWithdrawalRate,
  }), [currentAge, targetRetirementAge, annualExpenses, currentInvestments, annualSavings, expectedReturn, safeWithdrawalRate])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 14

    // --- Page 1: Title, Inputs, Key Results, Progress, Target Check ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text('FIRE Calculator Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)

    // Section: FIRE Inputs
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('FIRE Inputs', margin, 42)

    const inputRows = [
      ['Current Age', `${currentAge}`],
      ['Target Retirement Age', `${targetRetirementAge}`],
      ['Annual Expenses', formatCurrency(annualExpenses)],
      ['Current Investments', formatCurrency(currentInvestments)],
      ['Annual Savings', formatCurrency(annualSavings)],
      ['Expected Return', `${expectedReturn}%`],
      ['Safe Withdrawal Rate', `${safeWithdrawalRate}%`],
    ]

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: inputRows,
      startY: 46,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Key Results (stat cards)
    let y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Key Results', margin, y)
    y += 4

    const keyResultRows = [
      ['FI Number', formatCurrency(result.fiNumber)],
      ['Years to FI', result.yearsToFI ? `${result.yearsToFI} years` : 'N/A'],
      ['FI Age', result.fiAge ? `Age ${result.fiAge}` : 'N/A'],
      ['Savings Rate', `${result.savingsRate}%`],
    ]

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: keyResultRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Progress to FI (mirrors progress bar card)
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Progress to FI', margin, y)
    y += 6

    doc.setFontSize(10)
    doc.setTextColor(60)
    doc.text(`Current Progress: ${result.currentProgress}%`, margin, y)
    y += 5
    doc.text(`Current Investments: ${formatCurrency(result.currentInvestments)}`, margin, y)
    y += 5
    doc.text(`FI Number Target: ${formatCurrency(result.fiNumber)}`, margin, y)
    y += 5

    // Draw a simple progress bar
    const barWidth = pageWidth - margin * 2
    const barHeight = 6
    doc.setFillColor(230, 230, 230)
    doc.roundedRect(margin, y, barWidth, barHeight, 2, 2, 'F')
    const progressWidth = Math.min(100, result.currentProgress) / 100 * barWidth
    doc.setFillColor(59, 130, 246)
    if (progressWidth > 0) {
      doc.roundedRect(margin, y, progressWidth, barHeight, 2, 2, 'F')
    }
    y += barHeight + 8

    // Section: Target Check (mirrors the insight banner)
    doc.setFontSize(12)
    doc.setTextColor(0)
    doc.text('Target Assessment', margin, y)
    y += 6
    doc.setFontSize(10)
    doc.setTextColor(60)

    let targetText = ''
    if (result.canRetireByTarget) {
      targetText = `On track to reach FI by age ${result.fiAge}!`
    } else if (result.fiAge) {
      targetText = `FI at age ${result.fiAge} - ${result.fiAge - targetRetirementAge} years after your target of ${targetRetirementAge}`
    } else {
      targetText = 'Increase savings or reduce expenses to reach FI'
    }
    const splitTarget = doc.splitTextToSize(targetText, pageWidth - margin * 2)
    doc.text(splitTarget, margin, y)
    y += splitTarget.length * 5 + 2

    if (result.isCoastFI) {
      const coastText = `Coast FIRE achieved! Your current ${formatCurrency(result.currentInvestments)} exceeds the ${formatCurrency(result.coastFINumber)} needed to coast to FI with zero additional savings.`
      doc.setFontSize(9)
      doc.setTextColor(80)
      const splitCoast = doc.splitTextToSize(coastText, pageWidth - margin * 2)
      doc.text(splitCoast, margin, y)
      y += splitCoast.length * 4.5 + 2
    }

    // --- Page 2: SWR Sensitivity + Coast FIRE ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(0)
    doc.text('SWR Sensitivity', margin, 22)

    const sensitivityRows = result.sensitivityTable.map(row => [
      `${row.swr}%`,
      formatCurrency(row.fiNumber),
      row.yearsToFI ? `${row.yearsToFI} yrs` : 'N/A',
      row.fiAge ? `Age ${row.fiAge}` : 'N/A',
      row.swr === safeWithdrawalRate ? '(current)' : '',
    ])

    autoTable(doc, {
      head: [['SWR', 'FI Number', 'Years to FI', 'FI Age', '']],
      body: sensitivityRows,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      didParseCell: (data) => {
        // Highlight the current SWR row
        if (data.section === 'body' && result.sensitivityTable[data.row.index]?.swr === safeWithdrawalRate) {
          data.cell.styles.fillColor = [239, 246, 255]
        }
      },
    })

    // Section: Coast FIRE (mirrors the Coast FIRE card)
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Coast FIRE', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(100)
    doc.text(`The portfolio balance needed today to reach FI by ${targetRetirementAge} with zero additional savings`, margin, y)
    y += 7

    const coastProgress = Math.min(100, Math.round((result.currentInvestments / result.coastFINumber) * 100))

    const coastRows = [
      ['Coast FI Number', formatCurrency(result.coastFINumber)],
      ['Current Investments', formatCurrency(result.currentInvestments)],
      ['Coast FI Progress', result.isCoastFI ? `${coastProgress}% - Achieved!` : `${coastProgress}%`],
      ['Coast FI Status', result.isCoastFI ? 'Achieved' : 'Not yet reached'],
    ]

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: coastRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // --- Page 3: Portfolio Projection Table ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(0)
    doc.text('Portfolio Projection', margin, 22)

    // Sample key ages: current, every 5 years, target retirement, FI age, and last
    const projectionRows = result.projection
      .filter(p => p.age === currentAge || p.age === targetRetirementAge || p.age % 5 === 0 || p.age === result.fiAge)
      .filter((p, i, arr) => arr.findIndex(x => x.age === p.age) === i) // dedupe
      .sort((a, b) => a.age - b.age)
      .map(p => [
        `Age ${p.age}`,
        formatCurrency(p.balance),
        p.balance >= result.fiNumber ? 'FI Reached' : `${Math.round((p.balance / result.fiNumber) * 100)}%`,
        p.age === targetRetirementAge ? 'Target' : p.age === result.fiAge ? 'FI Age' : '',
      ])

    autoTable(doc, {
      head: [['Age', 'Portfolio Balance', 'FI Progress', 'Note']],
      body: projectionRows,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      didParseCell: (data) => {
        // Highlight FI age row
        if (data.section === 'body') {
          const row = projectionRows[data.row.index]
          if (row && row[3] === 'FI Age') {
            data.cell.styles.fillColor = [232, 245, 233]
          } else if (row && row[3] === 'Target') {
            data.cell.styles.fillColor = [255, 243, 224]
          }
        }
      },
    })

    // Chart capture (adds its own page)
    await addChartToPDF(doc, chartRef, { title: 'Portfolio Projection Chart' })

    doc.save(`fire-calculator-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">FIRE Inputs</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <InputField label="Current Age" value={currentAge} onChange={setCurrentAge} min={18} max={80} suffix="yrs" />
          <InputField label="Target Retirement Age" value={targetRetirementAge} onChange={setTargetRetirementAge} min={currentAge + 1} max={80} suffix="yrs" />
          <InputField label="Annual Expenses" value={annualExpenses} onChange={setAnnualExpenses} prefix="$" hint="What you spend per year in retirement" />
          <InputField label="Current Investments" value={currentInvestments} onChange={setCurrentInvestments} prefix="$" />
          <InputField label="Annual Savings" value={annualSavings} onChange={setAnnualSavings} prefix="$" hint="How much you invest per year" />
          <InputField label="Expected Return" value={expectedReturn} onChange={setExpectedReturn} suffix="%" step={0.5} />
          <InputField label="Safe Withdrawal Rate" value={safeWithdrawalRate} onChange={setSafeWithdrawalRate} suffix="%" step={0.25} hint="Traditional: 4%" />
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="FI Number" value={formatCurrency(result.fiNumber)} color={colors.primary} highlight />
        <ResultCard
          label="Years to FI"
          value={result.yearsToFI ? `${result.yearsToFI} years` : 'N/A'}
          color={result.canRetireByTarget ? colors.positive : colors.warning}
          highlight
        />
        <ResultCard
          label="FI Age"
          value={result.fiAge ? `Age ${result.fiAge}` : 'N/A'}
          color={result.canRetireByTarget ? colors.positive : colors.warning}
        />
        <ResultCard label="Savings Rate" value={`${result.savingsRate}%`} color={colors.positive} />
      </div>

      {/* Progress */}
      <GlassCard>
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-semibold text-heading">Progress to FI</h3>
          <span className="text-sm font-medium" style={{ color: result.currentProgress >= 100 ? colors.positive : colors.primary }}>
            {result.currentProgress}%
          </span>
        </div>
        <div className="w-full h-4 rounded-full bg-subtle overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(100, result.currentProgress)}%`,
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.positive})`,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted mt-2">
          <span>{formatCurrency(result.currentInvestments)}</span>
          <span>{formatCurrency(result.fiNumber)} FI Number</span>
        </div>
      </GlassCard>

      {/* Target Check */}
      <GlassCard className="!p-4">
        <div className="flex items-center gap-3">
          <span className="text-xl">{result.canRetireByTarget ? '\u2705' : '\u26A0\uFE0F'}</span>
          <div>
            <p className="text-sm font-semibold text-heading">
              {result.canRetireByTarget
                ? `On track to reach FI by age ${result.fiAge}!`
                : result.fiAge
                  ? `FI at age ${result.fiAge} — ${result.fiAge - targetRetirementAge} years after your target of ${targetRetirementAge}`
                  : 'Increase savings or reduce expenses to reach FI'
              }
            </p>
            {result.isCoastFI && (
              <p className="text-xs text-muted mt-1">
                Coast FIRE achieved! Your current {formatCurrency(result.currentInvestments)} exceeds the {formatCurrency(result.coastFINumber)} needed to coast to FI with zero additional savings.
              </p>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Projection Chart */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Portfolio Projection</h3>
        <div className="h-64" ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={result.projection}>
              <defs>
                <linearGradient id="fireGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis dataKey="age" tick={{ fontSize: 11, fill: colors.chartTick }} label={{ value: 'Age', position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: colors.chartTick } }} />
              <YAxis tickFormatter={v => `$${formatCompactNumber(v)}`} tick={{ fontSize: 11, fill: colors.chartTick }} />
              <Tooltip
                formatter={(v, name) => [formatCurrency(v), name === 'balance' ? 'Portfolio' : 'FI Number']}
                labelFormatter={l => `Age ${l}`}
                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }}
              />
              <ReferenceLine y={result.fiNumber} stroke={colors.positive} strokeDasharray="5 5" label={{ value: `FI: ${formatCurrency(result.fiNumber)}`, fill: colors.positive, fontSize: 11 }} />
              {targetRetirementAge && <ReferenceLine x={targetRetirementAge} stroke={colors.warning} strokeDasharray="5 5" label={{ value: `Target: ${targetRetirementAge}`, fill: colors.warning, fontSize: 11 }} />}
              <Area type="monotone" dataKey="balance" stroke={colors.primary} fill="url(#fireGrad)" strokeWidth={2} name="Portfolio" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Sensitivity Table */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">SWR Sensitivity</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-card">
                <th className="text-left py-2 text-muted font-medium">SWR</th>
                <th className="text-right py-2 text-muted font-medium">FI Number</th>
                <th className="text-right py-2 text-muted font-medium">Years to FI</th>
                <th className="text-right py-2 text-muted font-medium">FI Age</th>
              </tr>
            </thead>
            <tbody>
              {result.sensitivityTable.map(row => (
                <tr key={row.swr} className={`border-b border-card/50 ${row.swr === safeWithdrawalRate ? 'bg-primary/5' : ''}`}>
                  <td className="py-2 text-heading">{row.swr}%</td>
                  <td className="py-2 text-right text-heading">{formatCurrency(row.fiNumber)}</td>
                  <td className="py-2 text-right text-heading">{row.yearsToFI ? `${row.yearsToFI} yrs` : 'N/A'}</td>
                  <td className="py-2 text-right" style={{ color: row.fiAge && row.fiAge <= targetRetirementAge ? colors.positive : colors.warning }}>
                    {row.fiAge ? `Age ${row.fiAge}` : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Coast FIRE */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-2">Coast FIRE</h3>
        <p className="text-xs text-muted mb-3">The portfolio balance needed today to reach FI by {targetRetirementAge} with zero additional savings</p>
        <div className="flex items-center gap-4">
          <div>
            <p className="text-sm text-muted">Coast FI Number</p>
            <p className="text-lg font-bold text-heading">{formatCurrency(result.coastFINumber)}</p>
          </div>
          <div className="flex-1 h-3 rounded-full bg-subtle overflow-hidden">
            <div className="h-full rounded-full" style={{
              width: `${Math.min(100, (result.currentInvestments / result.coastFINumber) * 100)}%`,
              backgroundColor: result.isCoastFI ? colors.positive : colors.primary,
            }} />
          </div>
          <span className="text-sm font-medium" style={{ color: result.isCoastFI ? colors.positive : colors.primary }}>
            {result.isCoastFI ? 'Achieved!' : `${Math.round((result.currentInvestments / result.coastFINumber) * 100)}%`}
          </span>
        </div>
      </GlassCard>
    </div>
  )
}
